// Generated by GitHub Copilot
import { hasAuthenticatedSession, resolveCompanySlug } from '#core/auth/public-session.ts'
import { type WorkOrderPriority } from '#core/entities/operations/work-orders/WorkOrder.ts'
import { useComponentState } from '#core/state/State.ts'
import { customPropertyKeys } from '#core/entities/shared/custom-properties.ts'
import { missingRequiredCustomKeys } from '#core/utils/required-fields.ts'
import { CustomPropertiesEditor } from '#components/composites/custom-properties-editor.tsx'
import { MultiFileInput } from '#components/primitives/forms/multi-file-input.tsx'
import { WorkOrderPrioritySegmented } from '#components/domain/operations/work-orders/shared/work-order-edit/work-order-priority-segmented.tsx'
import { PublicWorkRequestAssetField } from '#components/domain/operations/work-requests/work-request-public/public-work-request-asset-field.tsx'
import { PublicWorkRequestState } from '#components/domain/operations/work-requests/work-request-public/public-work-request-state.ts'
import { LanguageSelect } from '#components/composites/language-select.tsx'
import { cn } from '#core/utils/cn.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import i18n from '#i18n.ts'

/** Loose client-side email check; the backend re-validates format on token mint. */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export const Route = createFileRoute('/_public/work-request/$companyId')({
  validateSearch: (search: Record<string, unknown>) => ({
    assetId: (search['assetId'] as string | undefined) ?? undefined,
  }),
  // Preload namespaces so t() doesn't return raw keys on first paint.
  loader: () => i18n.loadNamespaces(['work-requests', 'shared']),
  component: PublicWorkRequestPage,
})

function PublicWorkRequestPage() {
  const { t, ready } = useTranslation(['work-requests', 'shared'])
  const { companyId } = Route.useParams()
  const { assetId } = Route.useSearch()
  const navigate = useNavigate()
  const state = useComponentState(PublicWorkRequestState)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [priority, setPriority] = useState<WorkOrderPriority | ''>('')
  const [files, setFiles] = useState<File[]>([])
  const [customProperties, setCustomProperties] = useState<Record<string, unknown> | null>(null)

  // A logged-in user must not go through the anonymous guest flow (it would mint a
  // throwaway guest identity and overwrite their session). Send them into the app.
  const isAuthenticated = hasAuthenticatedSession()
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({
        to: '/$companyId/work-requests',
        params: { companyId: resolveCompanySlug(companyId) },
        search: { assetId },
        replace: true,
      })
      return
    }
    // Anonymous visitor: probe whether public submission is enabled before rendering the form.
    void state.checkAvailability(companyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The identity gate: once name + email are valid, mint the guest token (debounced so we don't
  // mint mid-keystroke). Editing back to an invalid value re-locks the rest of the form.
  const trimmedName = name.trim()
  const trimmedEmail = email.trim()
  const identityInputsValid = trimmedName.length >= 2 && isValidEmail(trimmedEmail)
  useEffect(() => {
    if (!state.available) return
    if (!identityInputsValid) {
      state.resetIdentity()
      return
    }
    const handle = setTimeout(() => {
      void state.provideIdentity(companyId, trimmedName, trimmedEmail, assetId)
    }, 500)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedName, trimmedEmail, identityInputsValid, state.available])

  // Hold rendering until i18next is ready to avoid a raw-key flash on slow connections.
  if (!ready) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      </div>
    )
  }

  // Title is always shown/required; name/email are the identity gate (not governed by settings).
  const showDescription = state.isFieldVisible('description')
  const requireDescription = showDescription && state.isFieldRequired('description')
  const showPriority = state.isFieldVisible('priority')
  const requirePriority = showPriority && state.isFieldRequired('priority')
  const showFiles = state.isFieldVisible('files')
  const requireFiles = showFiles && state.isFieldRequired('files')
  const submittedAssetId = state.submittedAssetId(assetId)
  const showCustomFields = customPropertyKeys(state.visibleCustomDefinition).length > 0

  const fieldsLocked = !state.identityReady

  const canSubmit =
    state.identityReady &&
    title.trim().length >= 2 &&
    !state.loading &&
    (!requireDescription || description.trim().length > 0) &&
    (!requirePriority || !!priority) &&
    (!requireFiles || files.length > 0) &&
    (!state.assetRequired || !!submittedAssetId) &&
    missingRequiredCustomKeys(state.visibleCustomDefinition, customProperties).length === 0

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    void state.submit(companyId, {
      title: title.trim(),
      description,
      name: trimmedName,
      email: trimmedEmail,
      priority,
      files,
      assetId: submittedAssetId,
      customProperties: showCustomFields ? customProperties : null,
    })
  }

  if (isAuthenticated) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        {t('shared.fields.redirecting')}
      </div>
    )
  }

  if (state.checking) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      </div>
    )
  }

  if (!state.available) {
    return (
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card">
              <div className="card-header d-flex justify-content-end">
                <LanguageSelect style={{ maxWidth: '84px' }} />
              </div>
              <div className="card-body text-center py-5">
                <h5 className="mb-2">{t('public.noAccess.title')}</h5>
                <p className="text-secondary mb-0">{t('public.noAccess.body')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between gap-2">
              <h5 className="mb-0">{t('public.title')}</h5>
              <LanguageSelect style={{ maxWidth: '84px' }} />
            </div>
            <div className="card-body">
              {state.success ? (
                <div className="alert alert-success" role="alert">
                  {t('public.success')}
                </div>
              ) : (
                <form onSubmit={onSubmit} className="d-flex flex-column gap-3">
                  {state.error && (
                    <div className="alert alert-danger mb-0" role="alert">
                      {t(state.error)}
                    </div>
                  )}

                  {/* Name / Email — the identity gate. Filling both mints the guest token that
                      unlocks the rest of the form (bound at token mint, not governed by settings). */}
                  <div>
                    <label className="form-label" htmlFor="wr-name">{t('public.nameField')} *</label>
                    <input
                      id="wr-name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={255}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label" htmlFor="wr-email">{t('public.emailField')} *</label>
                    <input
                      id="wr-email"
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={320}
                      required
                    />
                  </div>

                  {/* Gate status: hint while locked, spinner while minting, error on failure. */}
                  {fieldsLocked && (
                    <div className="d-flex align-items-center gap-2 small text-secondary" aria-live="polite">
                      {state.identityLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          {t('public.identity.preparing')}
                        </>
                      ) : state.identityError ? (
                        <span className="text-danger">{t(state.identityError)}</span>
                      ) : (
                        <>
                          <Lock size={14} className="flex-shrink-0" aria-hidden="true" />
                          {t('public.identity.hint')}
                        </>
                      )}
                    </div>
                  )}

                  {/* Everything below is locked until the guest identity exists. */}
                  <fieldset
                    disabled={fieldsLocked || state.loading}
                    className={cn('d-flex flex-column gap-3 border-0 p-0 m-0', fieldsLocked && 'opacity-50')}
                  >
                    {/* Title — always visible + required (no form-settings override). */}
                    <div>
                      <label className="form-label" htmlFor="wr-title">{t('public.titleField')} *</label>
                      <input
                        id="wr-title"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={100}
                        required
                      />
                    </div>

                    {showDescription && (
                      <div>
                        <label className="form-label" htmlFor="wr-description">
                          {t('shared.fields.description')}{requireDescription ? ' *' : ''}
                        </label>
                        <textarea
                          id="wr-description"
                          className="form-control"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          maxLength={1000}
                          rows={4}
                          required={requireDescription}
                        />
                      </div>
                    )}

                    {showPriority && (
                      <div>
                        <label className="form-label d-block" htmlFor="wr-priority">
                          {t('shared.fields.priority')}{requirePriority ? ' *' : ''}
                        </label>
                        <WorkOrderPrioritySegmented
                          value={priority}
                          onChange={(p) => setPriority(p)}
                          disabled={fieldsLocked || state.loading}
                          ariaLabel={t('shared.fields.priority')}
                        />
                      </div>
                    )}

                    {showFiles && (
                      <div>
                        <label className="form-label d-block">
                          {t('shared.fields.attachments')}{requireFiles ? ' *' : ''}
                        </label>
                        <MultiFileInput
                          files={files}
                          onChange={setFiles}
                          multiple
                          enableImageDraw
                          supportText={`${t('createDrawer.placeholders.files')} ${t('shared.placeholders.filesSupport')}`}
                          disabled={fieldsLocked || state.loading}
                        />
                      </div>
                    )}

                    <PublicWorkRequestAssetField state={state} assetId={assetId} />

                    {showCustomFields && (
                      <div>
                        <label className="form-label d-block">{t('shared.fields.customFields')}</label>
                        <div className="border rounded p-3">
                          <CustomPropertiesEditor
                            definition={state.visibleCustomDefinition}
                            value={customProperties}
                            onChange={setCustomProperties}
                            disabled={fieldsLocked || state.loading}
                            title=""
                          />
                        </div>
                      </div>
                    )}
                  </fieldset>

                  <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                    {state.loading && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    {t('shared.actions.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
